// HubSpotEmailsInfo.js

import React, { useState, useMemo } from "react";
import axios from "axios";
import Swal from "sweetalert2";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  TextField,
  MenuItem,
  Button,
  Chip,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Pagination,
  Stack,
  LinearProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  Tabs,
  Tab,
} from "@mui/material";

// Charts
import {
  Chart as ChartJS,
  ArcElement,
  Tooltip as ChartTooltip,
  Legend,
  CategoryScale,
  LinearScale,
  BarElement,
} from "chart.js";
import { Pie, Bar } from "react-chartjs-2";

import { baseURL } from "../backend_url";

// Register chart.js components
ChartJS.register(
  ArcElement,
  ChartTooltip,
  Legend,
  CategoryScale,
  LinearScale,
  BarElement
);

/* -----------------------------------------------------
   HELPERS
----------------------------------------------------- */

const formatDateTime = (val) =>
  val
    ? new Date(val).toLocaleString("en-GB", {
        timeZone: "UTC",
        hour12: false,
      }) + " UTC"
    : "-";

const formatMinutes = (mins) => {
  if (mins == null) return "N/A";
  const m = Math.round(Number(mins)); // round decimals
  const h = Math.floor(m / 60);
  const rem = m % 60;
  if (h && rem) return `${h}h ${rem}m`;
  if (h) return `${h}h`;
  return `${rem}m`;
};

const sentimentColor = (label) => {
  if (label === "positive") return "success";
  if (label === "negative") return "error";
  if (label === "neutral") return "default";
  return "default";
};

const sentimentLabelDisplay = (sentiment) => {
  if (!sentiment || !sentiment.label || sentiment.label === "N/A") return "N/A";
  const s = sentiment;
  return `${s.label}${s.stars ? ` (${s.stars}★)` : ""}`;
};

// Normalize backend → UI ticket format
const normalizeTickets = (tickets) =>
  tickets.map((t) => {
    const sentiment = t.sentimentLabel
      ? { label: t.sentimentLabel, stars: t.sentimentScore }
      : t.sentiment || { label: "N/A", stars: null };

    return {
      ...t,
      ticketId: t.ticketId || t.id,
      ownerName: t.ownerName || t.agentName || "",
      contactName: t.contactName || t.customerName || "",
      contactEmail: t.contactEmail || t.customerEmail || "",
      sentiment,
      agentId: t.agentId || t.ownerId || t.agent_id, // normalized agent ID
    };
  });

// Compute analytics for a list of tickets
const computeAnalytics = (tickets) => {
  const totalTickets = tickets.length;
  let totalFirst = 0;
  let countFirst = 0;
  let totalRes = 0;
  let countRes = 0;

  const sentimentCounts = {
    positive: 0,
    neutral: 0,
    negative: 0,
  };

  tickets.forEach((t) => {
    if (t.firstResponseMinutes != null) {
      totalFirst += Number(t.firstResponseMinutes);
      countFirst++;
    }
    if (t.resolutionMinutes != null) {
      totalRes += Number(t.resolutionMinutes);
      countRes++;
    }

    const label = t.sentiment?.label;
    if (label === "positive") sentimentCounts.positive++;
    else if (label === "negative") sentimentCounts.negative++;
    else if (label === "neutral") sentimentCounts.neutral++;
  });

  const avgFirstResponseMinutes =
    countFirst > 0 ? totalFirst / countFirst : null;
  const avgResolutionMinutes = countRes > 0 ? totalRes / countRes : null;

  return {
    totalTickets,
    avgFirstResponseMinutes,
    avgResolutionMinutes,
    sentimentCounts,
  };
};

// Tab color based on sentiment ratio
const sentimentDotColorFromAnalytics = (analytics) => {
  if (!analytics) return "#b0bec5";

  const total = analytics.totalTickets || 0;
  const pos = analytics.sentimentCounts?.positive || 0;
  const neg = analytics.sentimentCounts?.negative || 0;

  if (total === 0 || (pos === 0 && neg === 0)) {
    return "#b0bec5"; // grey
  }

  const relevant = pos + neg || 1;
  const ratio = pos / relevant;

  if (ratio >= 0.7) return "#2e7d32"; // green
  if (ratio <= 0.3) return "#c62828"; // red
  return "#f9a825"; // yellow / mixed
};

/* -----------------------------------------------------
   MAIN COMPONENT
----------------------------------------------------- */

export default function HubSpotEmailsInfo() {
  // Mode: particular agent or all
  const [mode, setMode] = useState("agent");
  const [agentId, setAgentId] = useState("");

  const [agents] = useState([
    { id: "77019651", name: "John Ben" },
    { id: "81060236", name: "Jaiwi Wilson" },
    { id: "83346074", name: "MJ Lakshmi Sagar" },
    { id: "83489430", name: "Ganesh Reddy" },
    { id: "83779333", name: "Sai Deekshith" },
    { id: "84507763", name: "Nagalakshmi NN" },
    { id: "84507789", name: "Sangeetha S" },
    { id: "352313353", name: "Lisa A" },
    { id: "415740507", name: "Kezina S" },
    { id: "517023755", name: "Dereck S" },
    { id: "691847439", name: "Sam Logan" },
    { id: "1064112585", name: "Rajesh Raghavan" },
  ]);

  const [fromDate, setFromDate] = useState(
    new Date(Date.now() - 6 * 86400000).toISOString().split("T")[0]
  );
  const [toDate, setToDate] = useState(new Date().toISOString().split("T")[0]);

  const [allRows, setAllRows] = useState([]); // full dataset response
  const [rows, setRows] = useState([]); // filtered (tab/agent + filters)
  const [searchText, setSearchText] = useState("");
  const [pipelineStageFilter, setPipelineStageFilter] = useState("all");

  const [analytics, setAnalytics] = useState(null);
  const [activeTab, setActiveTab] = useState("all");
  const [analyticsMap, setAnalyticsMap] = useState({});

  const [loading, setLoading] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);

  const [page, setPage] = useState(1);
  const limit = 15;

  const [selectedTicket, setSelectedTicket] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);

  const HUBSPOT_URL = `${baseURL}/hubspot/tickets`;

  /* -----------------------------------------------------
     VALIDATION
  ----------------------------------------------------- */
  const validate = () => {
    if (!fromDate || !toDate) {
      Swal.fire("Missing Fields", "From and To dates are required", "warning");
      return false;
    }

    if (mode === "agent" && !agentId) {
      Swal.fire("Missing Agent", "Please select an agent", "warning");
      return false;
    }

    return true;
  };

  /* -----------------------------------------------------
     SEARCH BUTTON → FETCH DATA ONE TIME
  ----------------------------------------------------- */
  const handleSearch = async () => {
    if (!validate()) return;

    setLoading(true);
    setHasSearched(true);
    setActiveTab("all");
    setPipelineStageFilter("all");
    setSearchText("");
    setPage(1);

    try {
      const params =
        mode === "agent"
          ? { from: fromDate, to: toDate, agentId }
          : { from: fromDate, to: toDate, agentId: "all" };

      // TODO (backend pagination optimization):
      // pass page & limit here when backend supports it, e.g.:
      // params.page = 1; params.limit = 500; etc.

      const { data } = await axios.get(HUBSPOT_URL, { params });

      const tickets = normalizeTickets(data.tickets || data.results || []);

      setAllRows(tickets);

      // Compute analytics for ALL tickets
      const allAnalytics = computeAnalytics(tickets);
      setAnalytics(allAnalytics);

      // Build analytics per agent (for tabs)
      const grouped = {};
      tickets.forEach((t) => {
        if (!t.agentId) return;
        if (!grouped[t.agentId]) grouped[t.agentId] = [];
        grouped[t.agentId].push(t);
      });

      const perAgent = {};
      Object.keys(grouped).forEach((aid) => {
        perAgent[aid] = computeAnalytics(grouped[aid]);
      });

      setAnalyticsMap({
        all: allAnalytics,
        ...perAgent,
      });

      setRows(tickets);
    } catch (err) {
      Swal.fire(
        "Error",
        err?.response?.data?.message || err.message,
        "error"
      );
    } finally {
      setLoading(false);
    }
  };

  /* -----------------------------------------------------
     APPLY TAB FILTER (LOCAL ONLY)
  ----------------------------------------------------- */
  const handleTabChange = (val) => {
    setActiveTab(val);
    setPage(1);

    if (val === "all") {
      setRows(allRows);
      setAnalytics(analyticsMap["all"]);
    } else {
      const filtered = allRows.filter((t) => t.agentId === val);
      setRows(filtered);
      setAnalytics(analyticsMap[val]);
    }
  };

  /* -----------------------------------------------------
     EXPORT CURRENT VIEW (FILTEREDROWS) TO CSV
  ----------------------------------------------------- */
  const handleExportCSV = () => {
    if (!filteredRows.length) {
      Swal.fire("No Data", "There is no data to export.", "info");
      return;
    }

    const header = [
      "Ticket ID",
      "Subject",
      "Agent",
      "Contact Name",
      "Contact Email",
      "Created At",
      "Priority",
      "Pipeline Stage",
      "First Response (mins)",
      "Resolution (mins)",
      "Sentiment",
    ];

    const rowsCsv = filteredRows.map((r) => [
      r.ticketId,
      r.subject,
      r.ownerName,
      r.contactName,
      r.contactEmail,
      formatDateTime(r.createdAt),
      r.priority || "",
      r.pipelineStage || "",
      r.firstResponseMinutes ?? "",
      r.resolutionMinutes ?? "",
      r.sentiment?.label || "",
    ]);

    const csvContent =
      [header, ...rowsCsv]
        .map((row) =>
          row
            .map((val) =>
              `"${String(val || "").replace(/"/g, '""')}"`
            )
            .join(",")
        )
        .join("\n");

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);

    const a = document.createElement("a");
    a.href = url;
    a.download = `hubspot_tickets_${Date.now()}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  /* -----------------------------------------------------
     SEARCH / FILTER PIPELINE + TEXT
  ----------------------------------------------------- */
  const filteredRows = useMemo(() => {
    let out = rows;

    // Pipeline Stage filter
    if (pipelineStageFilter !== "all") {
      out = out.filter((r) => r.pipelineStage === pipelineStageFilter);
    }

    // Search text filter
    const term = searchText.trim().toLowerCase();
    if (term) {
      out = out.filter((r) =>
        [
          r.subject,
          r.ownerName,
          r.contactName,
          r.contactEmail,
          r.priority,
          r.pipelineStage,
        ]
          .filter(Boolean)
          .some((v) => String(v).toLowerCase().includes(term))
      );
    }

    return out;
  }, [rows, searchText, pipelineStageFilter]);

  const paginatedRows = filteredRows.slice((page - 1) * limit, page * limit);

  /* -----------------------------------------------------
     CHART DATA (Sentiment + SLA)
  ----------------------------------------------------- */
  const sentimentChartData =
    analytics && analytics.sentimentCounts
      ? {
          labels: ["Positive", "Neutral", "Negative"],
          datasets: [
            {
              data: [
                analytics.sentimentCounts.positive || 0,
                analytics.sentimentCounts.neutral || 0,
                analytics.sentimentCounts.negative || 0,
              ],
              backgroundColor: ["#2e7d32", "#bdbdbd", "#c62828"],
            },
          ],
        }
      : null;

  const slaChartData =
    analytics && (analytics.avgFirstResponseMinutes || analytics.avgResolutionMinutes)
      ? {
          labels: ["Avg First Response", "Avg Resolution"],
          datasets: [
            {
              label: "Minutes",
              data: [
                Math.round(analytics.avgFirstResponseMinutes || 0),
                Math.round(analytics.avgResolutionMinutes || 0),
              ],
              backgroundColor: ["#1976d2", "#1565c0"],
            },
          ],
        }
      : null;

  /* -----------------------------------------------------
     RENDER
  ----------------------------------------------------- */

  return (
    <>
      {/* TICKET MODAL */}
      <Dialog
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle>
          {selectedTicket
            ? `Ticket #${selectedTicket.ticketId} — ${selectedTicket.subject}`
            : "Ticket"}
        </DialogTitle>

        <DialogContent dividers>
          {selectedTicket && (
            <Box>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Customer: <b>{selectedTicket.contactName}</b>{" "}
                {selectedTicket.contactEmail
                  ? `(${selectedTicket.contactEmail})`
                  : ""}
              </Typography>

              <Typography variant="subtitle2" sx={{ mb: 2 }}>
                Sentiment:{" "}
                <Chip
                  label={sentimentLabelDisplay(selectedTicket.sentiment)}
                  color={sentimentColor(selectedTicket.sentiment?.label)}
                  size="small"
                />
              </Typography>

              <Box
                sx={{
                  maxHeight: 500,
                  overflowY: "auto",
                  borderRadius: 1,
                  border: "1px solid #eee",
                  p: 2,
                  backgroundColor: "#fafafa",
                }}
              >
                {selectedTicket.conversation &&
                selectedTicket.conversation.length > 0 ? (
                  selectedTicket.conversation.map((msg, idx) => (
                    <Box
                      key={idx}
                      sx={{
                        mb: 2,
                        display: "flex",
                        justifyContent:
                          msg.direction === "agent" ? "flex-end" : "flex-start",
                      }}
                    >
                      <Box
                        sx={{
                          maxWidth: "80%",
                          p: 1.5,
                          borderRadius: 2,
                          backgroundColor:
                            msg.direction === "agent" ? "#e3f2fd" : "#fff",
                          boxShadow: "0 1px 4px rgba(0,0,0,0.08)",
                        }}
                      >
                        <Typography
                          variant="caption"
                          sx={{ color: "gray", mb: 1, display: "block" }}
                        >
                          {msg.direction === "agent" ? "Agent" : "Customer"} •{" "}
                          {msg.fromName || msg.fromEmail}
                        </Typography>
                        <Typography sx={{ whiteSpace: "pre-wrap" }}>
                          {msg.text}
                        </Typography>
                      </Box>
                    </Box>
                  ))
                ) : (
                  <Typography variant="body2" sx={{ whiteSpace: "pre-wrap" }}>
                    {selectedTicket.description ||
                      "(No description available for this ticket)"}
                  </Typography>
                )}
              </Box>
            </Box>
          )}
        </DialogContent>
      </Dialog>

      {/* MAIN UI */}
      <Box
        p={4}
        sx={{
          minHeight: "100vh",
          background: "linear-gradient(135deg, #f5f7fb 0%, #e3f2fd 100%)",
        }}
      >
        <Card
          elevation={4}
          sx={{
            maxWidth: 1300,
            mx: "auto",
            borderRadius: 3,
            boxShadow: "0 10px 30px rgba(0,0,0,0.08)",
          }}
        >
          {loading && <LinearProgress />}

          <CardContent>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                mb: 2,
              }}
            >
              <Typography variant="h5" fontWeight="bold">
                📧 HubSpot Email / Ticket Analytics
              </Typography>
              {rows.length > 0 && (
                <Button
                  variant="outlined"
                  size="small"
                  onClick={handleExportCSV}
                >
                  Export CSV
                </Button>
              )}
            </Box>

            {/* MODE + AGENT + DATES + PIPELINE FILTER */}
            <Grid
              container
              spacing={2}
              sx={{
                mb: 2,
                p: 2,
                borderRadius: 2,
                backgroundColor: "#fafafa",
                border: "1px solid #e0e0e0",
              }}
            >
              <Grid item xs={12} sm={3}>
                <TextField
                  fullWidth
                  select
                  label="Mode"
                  value={mode}
                  size="small"
                  onChange={(e) => {
                    setMode(e.target.value);
                    setAgentId("");
                  }}
                >
                  <MenuItem value="agent">Particular Agent</MenuItem>
                  <MenuItem value="all_agents">All Agents</MenuItem>
                </TextField>
              </Grid>

              {mode === "agent" && (
                <Grid item xs={12} sm={3}>
                  <TextField
                    fullWidth
                    select
                    size="small"
                    label="Select Agent"
                    value={agentId}
                    onChange={(e) => setAgentId(e.target.value)}
                  >
                    {agents.map((a) => (
                      <MenuItem key={a.id} value={a.id}>
                        {a.name}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
              )}

              <Grid item xs={6} sm={2}>
                <TextField
                  fullWidth
                  size="small"
                  label="From"
                  type="date"
                  value={fromDate}
                  onChange={(e) => setFromDate(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>

              <Grid item xs={6} sm={2}>
                <TextField
                  fullWidth
                  size="small"
                  label="To"
                  type="date"
                  value={toDate}
                  onChange={(e) => setToDate(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>

              <Grid item xs={12} sm={2}>
                <TextField
                  fullWidth
                  select
                  size="small"
                  label="Pipeline Stage"
                  value={pipelineStageFilter}
                  onChange={(e) => {
                    setPipelineStageFilter(e.target.value);
                    setPage(1);
                  }}
                >
                  <MenuItem value="all">All Stages</MenuItem>
                  {[...new Set(allRows.map((r) => r.pipelineStage))]
                    .filter(Boolean)
                    .map((stage) => (
                      <MenuItem key={stage} value={stage}>
                        {stage}
                      </MenuItem>
                    ))}
                </TextField>
              </Grid>
            </Grid>

            {/* SEARCH BUTTONS */}
            <Box sx={{ display: "flex", gap: 2, mb: 2 }}>
              <Button
                variant="contained"
                onClick={handleSearch}
                disabled={loading}
                sx={{ textTransform: "none" }}
              >
                {loading ? <CircularProgress size={20} /> : "Search"}
              </Button>
            </Box>

            {/* TABS (Only when mode=all_agents and data exists) */}
            {mode === "all_agents" &&
              hasSearched &&
              allRows.length > 0 && (
                <Box sx={{ mb: 2, borderBottom: "1px solid #ddd" }}>
                  <Tabs
                    value={activeTab}
                    onChange={(e, val) => handleTabChange(val)}
                    variant="scrollable"
                    allowScrollButtonsMobile
                  >
                    <Tab
                      value="all"
                      label="All Agents"
                      style={{
                        borderTop: `3px solid ${sentimentDotColorFromAnalytics(
                          analyticsMap["all"]
                        )}`,
                      }}
                    />
                    {Object.keys(analyticsMap)
                      .filter((aid) => aid !== "all")
                      .map((aid) => {
                        const agentName =
                          agents.find((a) => a.id === aid)?.name || aid;
                        return (
                          <Tab
                            key={aid}
                            value={aid}
                            label={agentName}
                            style={{
                              borderTop: `3px solid ${sentimentDotColorFromAnalytics(
                                analyticsMap[aid]
                              )}`,
                            }}
                          />
                        );
                      })}
                  </Tabs>
                </Box>
              )}

            {/* SEARCH WITHIN RESULTS */}
            {rows.length > 0 && (
              <TextField
                sx={{ mb: 2 }}
                fullWidth
                size="small"
                placeholder="Search within results…"
                value={searchText}
                onChange={(e) => {
                  setSearchText(e.target.value);
                  setPage(1);
                }}
              />
            )}

            {/* ANALYTICS + CHARTS */}
            {analytics && rows.length > 0 && (
              <Box sx={{ mb: 3 }}>
                <Box
                  sx={{
                    mb: 2,
                    p: 2,
                    backgroundColor: "#e3f2fd",
                    borderRadius: 2,
                    display: "flex",
                    flexWrap: "wrap",
                    gap: 2,
                    justifyContent: "space-between",
                  }}
                >
                  <Typography variant="subtitle2">
                    🧾 Total Tickets: <b>{analytics.totalTickets}</b>
                  </Typography>
                  <Typography variant="subtitle2">
                    ⏱ Avg First Response:{" "}
                    <b>{formatMinutes(analytics.avgFirstResponseMinutes)}</b>
                  </Typography>
                  <Typography variant="subtitle2">
                    ✅ Avg Resolution:{" "}
                    <b>{formatMinutes(analytics.avgResolutionMinutes)}</b>
                  </Typography>
                  <Typography variant="subtitle2">
                    😊 Positive:{" "}
                    <b>{analytics.sentimentCounts.positive || 0}</b> | 😐
                    Neutral: <b>{analytics.sentimentCounts.neutral || 0}</b> | 😡
                    Negative: <b>{analytics.sentimentCounts.negative || 0}</b>
                  </Typography>
                </Box>

                {/* {(sentimentChartData || slaChartData) && (
                  <Grid container spacing={2} sx={{ mb: 2 }}>
                    {sentimentChartData && (
                      <Grid item xs={12} md={6}>
                        <Paper sx={{ p: 2 }}>
                          <Typography
                            variant="subtitle2"
                            sx={{ mb: 1 }}
                          >
                            Sentiment Distribution
                          </Typography>
                          <Pie data={sentimentChartData} />
                        </Paper>
                      </Grid>
                    )}
                    {slaChartData && (
                      <Grid item xs={12} md={6}>
                        <Paper sx={{ p: 2 }}>
                          <Typography
                            variant="subtitle2"
                            sx={{ mb: 1 }}
                          >
                            Response & Resolution Time (mins)
                          </Typography>
                          <Bar data={slaChartData} />
                        </Paper>
                      </Grid>
                    )}
                  </Grid>
                )} */}
              </Box>
            )}

            {/* TABLE */}
            {filteredRows.length > 0 ? (
              <>
                {/* <TableContainer
                  component={Paper}
                  sx={{ borderRadius: 2, overflow: "hidden" }}
                > */}
                <TableContainer
                    component={Paper}
                    sx={{
                        borderRadius: 2,
                        overflowX: "auto",           // <── enables horizontal scroll
                        overflowY: "hidden",
                        maxWidth: "100%",
                        whiteSpace: "nowrap",        // <── prevents column wrapping
                    }}
                    >
                  <Table stickyHeader sx={{ minWidth: 1300 }}>
                    <TableHead>
                      <TableRow>
                        {[
                          "#",
                          "Subject",
                          "Agent",
                          "Contact",
                          "Created",
                          "Priority",
                          "Stage",
                          "First Response",
                          "Resolution",
                          "Sentiment",
                          "View",
                        ].map((h, i) => (
                          <TableCell
                            key={i}
                            align="center"
                            sx={{
                              backgroundColor: "#1976d2",
                              color: "#fff",
                              fontWeight: "bold",
                              borderBottom: "1px solid #bbdefb",
                            }}
                          >
                            {h}
                          </TableCell>
                        ))}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {paginatedRows.map((r, i) => (
                        <TableRow
                          key={r.ticketId || i}
                          sx={{
                            "&:nth-of-type(odd)": {
                              backgroundColor: "#fafafa",
                            },
                            "&:hover": {
                              backgroundColor: "#f0f7ff",
                              transition: "0.2s",
                            },
                          }}
                        >
                          <TableCell align="center">
                            {(page - 1) * limit + i + 1}
                          </TableCell>
                          <TableCell align="left">{r.subject}</TableCell>
                          <TableCell align="center">
                            {r.ownerName || "Unassigned"}
                          </TableCell>
                          <TableCell align="center">
                            {r.contactName}
                            {r.contactEmail ? ` (${r.contactEmail})` : ""}
                          </TableCell>
                          <TableCell align="center">
                            {formatDateTime(r.createdAt)}
                          </TableCell>
                          <TableCell align="center">
                            {r.priority || "-"}
                          </TableCell>
                          <TableCell align="center">
                            {r.pipelineStage || "-"}
                          </TableCell>
                          <TableCell align="center">
                            {formatMinutes(r.firstResponseMinutes)}
                          </TableCell>
                          <TableCell align="center">
                            {formatMinutes(r.resolutionMinutes)}
                          </TableCell>
                          <TableCell align="center">
                            <Chip
                              size="small"
                              label={sentimentLabelDisplay(r.sentiment)}
                              color={sentimentColor(r.sentiment?.label)}
                            />
                          </TableCell>
                          <TableCell align="center">
                            <Button
                              size="small"
                              variant="outlined"
                              onClick={() => {
                                setSelectedTicket(r);
                                setModalOpen(true);
                              }}
                            >
                              View
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>

                {/* Pagination */}
                <Stack mt={2} alignItems="center">
                  <Pagination
                    count={Math.ceil(filteredRows.length / limit)}
                    page={page}
                    onChange={(e, v) => setPage(v)}
                  />
                </Stack>
              </>
            ) : (
              hasSearched &&
              !loading && (
                <Paper
                  sx={{
                    p: 4,
                    textAlign: "center",
                    borderRadius: 2,
                    mt: 2,
                    backgroundColor: "#fafafa",
                    border: "1px dashed #b0bec5",
                  }}
                >
                  <Typography variant="h6" gutterBottom>
                    No tickets found
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Try changing the date range, mode, agent, pipeline stage or
                    search text.
                  </Typography>
                </Paper>
              )
            )}
          </CardContent>
        </Card>
      </Box>
    </>
  );
}
